import React, { useState } from 'react';
import { ShoppingBag, X, Minus, Plus, Loader2, CheckCircle2, AlertCircle, Wine, Crown, MapPin } from 'lucide-react';
import { useReservationCart } from './ReservationCartContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_EMAIL = 'everything@8eight8.net';

const fmtPrice = (n) => `$${n.toFixed(2).replace(/\.00$/, '')}`;

const ReservationCheckout = () => {
  const cart = useReservationCart();
  const [form, setForm] = useState({ name: '', email: '', phone: '', pickup: 'Tamura\'s · 10th Ave', notes: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  if (!cart.isCheckoutOpen) return null;

  const update = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          notes: `Pickup location: ${form.pickup}${form.notes.trim() ? `\n\n${form.notes.trim()}` : ''}`,
          items: cart.items.map((i) => ({
            brand: i.brand,
            expression: i.expression,
            quantity: i.quantity,
            price: i.price,
          })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Reservation could not be submitted');
      }
      const data = await res.json();
      setConfirmation(data);
      setStatus('success');
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('bottle_reservation', { bottles: cart.totalQty, total: cart.subtotal });
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Reservation could not be submitted. Please try the email fallback below.');
    }
  };

  const mailtoFallback = () => {
    const itemLines = cart.items.map((i) => `• ${i.brand} ${i.expression} × ${i.quantity}${i.price ? ` (${fmtPrice(i.price)} ea)` : ''}`).join('\n');
    const body = `Aloha — I'd like to reserve the following bottles for pickup at the Aloha Tequila & Arts Festival on June 20, 2026:\n\n${itemLines}\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\nNotes:\n${form.notes}\n\nEstimated total: ${fmtPrice(cart.subtotal)} (paid onsite at Tamura's booth)`;
    return `mailto:${FALLBACK_EMAIL}?subject=${encodeURIComponent('Bottle Reservation — Aloha Tequila & Arts Festival')}&body=${encodeURIComponent(body)}`;
  };

  const closeAndReset = () => {
    cart.closeCheckout();
    if (status === 'success') {
      cart.clear();
      setForm({ name: '', email: '', phone: '', pickup: "Tamura's · 10th Ave", notes: '' });
      setStatus('idle');
      setConfirmation(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4" data-testid="reservation-checkout-modal" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={status !== 'submitting' ? closeAndReset : undefined} />

      {/* Panel */}
      <div className="relative w-full sm:max-w-2xl max-h-[100dvh] sm:max-h-[90vh] bg-gradient-to-b from-[#1a0a14] to-[#0a0a0a] sm:rounded-3xl border-t sm:border border-white/10 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-7 pt-5 sm:pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/30">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-black text-white">Order Bottles for Pickup</h2>
              <p className="text-white/55 text-xs sm:text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Tamura&apos;s (10th Ave) · Fujioka&apos;s (Kapolei)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeAndReset}
            disabled={status === 'submitting'}
            data-testid="reservation-close-btn"
            className="text-white/40 hover:text-white/90 transition-colors p-2 -m-2 disabled:opacity-30"
            aria-label="Close reservation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
          {status === 'success' && confirmation ? (
            <div className="text-center py-6" data-testid="reservation-success">
              <div className="w-16 h-16 bg-green-500/15 border border-green-500/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-green-400" />
              </div>
              <h3 className="text-2xl font-black text-white mb-2">Mahalo — order received!</h3>
              <p className="text-white/70 text-sm sm:text-base mb-5 max-w-md mx-auto">
                We&apos;ve sent your bottle order to the team. Pick it up at{' '}
                <strong className="text-pink-300">{form.pickup}</strong>.
                Bring this confirmation number when you go:
              </p>
              <div className="inline-block bg-amber-500/15 border border-amber-500/40 rounded-2xl px-6 py-3 mb-5">
                <div className="text-amber-300 text-xs font-bold uppercase tracking-wider">Confirmation #</div>
                <div className="text-white font-black text-xl tabular-nums" data-testid="reservation-confirmation-number">{confirmation.confirmation_number}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 text-left max-w-md mx-auto">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-white/60">{confirmation.items.length} brand{confirmation.items.length !== 1 ? 's' : ''} · {cart.totalQty || confirmation.items.reduce((a,i)=>a+i.quantity,0)} bottle{cart.totalQty > 1 ? 's' : ''}</span>
                  <span className="text-pink-300 font-bold">{fmtPrice(confirmation.estimated_total)}</span>
                </div>
                <div className="text-xs text-white/50 mt-2">Pay at <strong className="text-white/70">any check-in or either of the main bars</strong> at the festival. Pickup at <strong className="text-white/70">{form.pickup}</strong>. Must be 21+ with valid government-issued ID. Subject to bottle availability.</div>
              </div>
              <Button
                onClick={closeAndReset}
                data-testid="reservation-done-btn"
                className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold rounded-full px-8 py-2.5"
              >
                Done
              </Button>
            </div>
          ) : cart.items.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <Wine className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Your reservation is empty. Tap <strong className="text-white/70">+ Reserve</strong> on any bottle to add it.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reservation-form">
              {/* Items */}
              <div className="space-y-2">
                {cart.items.map((it) => (
                  <div key={`${it.brand}::${it.expression}`} className="flex items-center gap-3 bg-white/[0.04] border border-white/10 rounded-xl p-3" data-testid={`cart-row-${it.brand}-${it.expression}`.toLowerCase().replace(/[^a-z0-9-]+/g,'-')}>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm sm:text-base truncate flex items-center gap-1.5">
                        {it.vip && <Crown className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                        {it.brand}
                      </div>
                      <div className="text-white/55 text-xs truncate">
                        {it.expression}{it.price ? ` · ${fmtPrice(it.price)} ea` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-white/5 rounded-full border border-white/10 shrink-0">
                      <button type="button" aria-label="Decrease" onClick={() => cart.setQuantity(it.brand, it.expression, it.quantity - 1)} className="w-7 h-7 flex items-center justify-center text-white/80 hover:text-pink-400">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-6 text-center text-white font-bold tabular-nums text-sm">{it.quantity}</span>
                      <button type="button" aria-label="Increase" onClick={() => cart.setQuantity(it.brand, it.expression, it.quantity + 1)} className="w-7 h-7 flex items-center justify-center text-white/80 hover:text-pink-400">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button type="button" onClick={() => cart.removeItem(it.brand, it.expression)} aria-label="Remove" className="text-white/30 hover:text-red-400 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-amber-300 text-xs font-bold uppercase tracking-wider">Estimated total</div>
                  <div className="text-white/55 text-xs">Pay onsite · {cart.totalQty} bottle{cart.totalQty !== 1 ? 's' : ''}</div>
                </div>
                <div className="text-white font-black text-2xl tabular-nums">{fmtPrice(cart.subtotal)}</div>
              </div>

              {/* Customer Info */}
              <div className="space-y-3 pt-2">
                <div>
                  <Label htmlFor="rsv-name" className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Full name</Label>
                  <Input id="rsv-name" data-testid="rsv-name" required value={form.name} onChange={update('name')} placeholder="Jane Aloha" className="bg-white/5 border-white/15 text-white h-11 rounded-xl" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rsv-email" className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Email</Label>
                    <Input id="rsv-email" data-testid="rsv-email" type="email" required value={form.email} onChange={update('email')} placeholder="you@email.com" className="bg-white/5 border-white/15 text-white h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="rsv-phone" className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Phone</Label>
                    <Input id="rsv-phone" data-testid="rsv-phone" type="tel" required value={form.phone} onChange={update('phone')} placeholder="808-555-1234" className="bg-white/5 border-white/15 text-white h-11 rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Pickup location</Label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[
                      { id: "Tamura's · 10th Ave",  label: "Tamura's",  sub: "10th Ave" },
                      { id: "Fujioka's · Kapolei",  label: "Fujioka's", sub: "Kapolei" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.id}
                        data-testid={`rsv-pickup-${opt.id.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        onClick={() => setForm((p) => ({ ...p, pickup: opt.id }))}
                        className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                          form.pickup === opt.id
                            ? 'bg-pink-500/20 border-pink-500/60 shadow-md shadow-pink-500/20'
                            : 'bg-white/5 border-white/15 hover:bg-white/10 hover:border-white/25'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          {form.pickup === opt.id ? <CheckCircle2 className="w-3.5 h-3.5 text-pink-300 shrink-0" /> : <MapPin className="w-3.5 h-3.5 text-white/40 shrink-0" />}
                          <span className={`text-sm font-bold ${form.pickup === opt.id ? 'text-white' : 'text-white/85'}`}>{opt.label}</span>
                        </div>
                        <div className={`text-[11px] mt-0.5 ml-5 ${form.pickup === opt.id ? 'text-pink-200' : 'text-white/55'}`}>{opt.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="rsv-notes" className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Notes <span className="text-white/30 normal-case tracking-normal">(optional)</span></Label>
                  <Textarea id="rsv-notes" data-testid="rsv-notes" rows={2} value={form.notes} onChange={update('notes')} placeholder="Gift wrap? Specific bottle number? Anything else…" className="bg-white/5 border-white/15 text-white rounded-xl" />
                </div>
              </div>

              {/* Error */}
              {status === 'error' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm flex items-start gap-2" data-testid="reservation-error">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <div className="text-red-200">
                    {errorMsg} —{' '}
                    <a href={mailtoFallback()} className="underline font-semibold">email us directly</a>
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <p className="text-white/40 text-[11px] leading-relaxed">
                21+ event · Pay at <strong className="text-white/55">any check-in or either of the main bars</strong> at the festival. Bottles held subject to availability — bring valid government-issued ID to pick up at your selected location.
              </p>

              {/* Submit */}
              <Button
                type="submit"
                disabled={status === 'submitting' || cart.items.length === 0}
                data-testid="reservation-submit-btn"
                className="w-full bg-gradient-to-r from-pink-500 via-orange-500 to-amber-500 hover:from-pink-600 hover:via-orange-600 hover:to-amber-600 text-white font-black h-12 rounded-full text-base shadow-lg shadow-pink-500/30"
              >
                {status === 'submitting' ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sending…</span>
                ) : (
                  <span className="flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Order {cart.totalQty} bottle{cart.totalQty !== 1 ? 's' : ''} · {fmtPrice(cart.subtotal)}</span>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationCheckout;
